// Utility to build a string n number of times
export const renderStringNTimes = (string: string, total: number) =>
  Array.from(new Array(total), (_, i) => i)
    .map(() => string)
    .join('')

// Visible for testing only
export const renderSpacingOrEmptyString = (index: number, total: number) =>
  index > 0 ? renderStringNTimes('  ', total) : ''

// Visible for testing only
export const buildGraphQLVariable = (variableName: string) => `$${variableName}`

type MapperFn<TFieldConfigKey extends string> = (
  fieldConfigKey: TFieldConfigKey,
  index: number
) => string

type FieldConfigString<TFieldConfigKey extends string> = Record<
  TFieldConfigKey,
  FieldConfigStringValue
>

// Visible for testing only
export function buildDefinition<TFieldConfigKey extends string>(
  fieldsConfig: FieldConfigString<TFieldConfigKey>,
  mapper: MapperFn<TFieldConfigKey>,
  separator: '\n' | ',\n'
) {
  return Object.keys(fieldsConfig).map<string>(mapper).join(separator)
}

/**
 * @description Builds the string that represents the arguments for a mutation. Visible for testing only.
 * @example
 * // Example usage
 * const output = buildArgsDefinition({
 *   organizationID: { typeString: 'ID!' },
 *   libraryHashesToExclude: { typeString: '[String]!' },
 *   applicationID: { typeString: 'String!' }
 * })
 * @example
 * // Example output
 * $organizationID: ID!
 * $libraryHashesToExclude: [String]!
 * $applicationID: String!
 */
export function buildArgsDefinition<TFieldConfigKey extends string>(
  fieldsConfig: FieldConfigString<TFieldConfigKey>
) {
  return buildDefinition(
    fieldsConfig,
    (fieldConfigKey, index) =>
      `${renderSpacingOrEmptyString(index, 2)}${buildGraphQLVariable(
        fieldConfigKey
      )}: ${fieldsConfig[fieldConfigKey].typeString}`,
    '\n'
  )
}

/**
 * @description Builds the string that represents the input arguments for a mutation. Visible for testing only.
 * @example
 * // Example usage
 * const output = buildArgsDefinition({
 *   organizationID: { typeString: 'ID!' },
 *   libraryHashesToExclude: { typeString: '[String]!' },
 *   applicationID: { typeString: 'String!' }
 * })
 * @example
 * // Example output
 * organizationID: $organizationID
 * libraryHashesToExclude: $libraryHashesToExclude
 * applicationID: $applicationID
 */
export function buildInputDefinition<TFieldConfigKey extends string>(
  fieldsConfig: FieldConfigString<TFieldConfigKey>
) {
  return buildDefinition(
    fieldsConfig,
    (fieldConfigKey, index) =>
      `${renderSpacingOrEmptyString(
        index,
        4
      )}${fieldConfigKey}: ${buildGraphQLVariable(fieldConfigKey)}`,
    ',\n'
  )
}

interface FieldConfigStringValue {
  readonly typeString: string
}

/**
 * @description
 * Visible for testing only
 *
 * Builds mutation string that is used in the view layer to pass to graphql
 * This is automated to reduce a range of bugs that are easily introduced by
 * mistyping GraphQL arguments, variables, and input values.
 *
 * Only a true end-to-end test will prevent these bugs by actually exercising
 * the code that uses the mutation and the code that executes it. Even in our
 * automated integration-style tests, which use jest and MockedProvider, this
 * integration between the view and the GraphQL code is not covered.
 *
 * Then, in tests, you should generate a snapshot of the `mutation` that is produced
 * by the `buildMutation` function. This makes it easy to review in a pull request
 * and it also provides a convenient way to manually copy the mutation string so that
 * you can share it easily and use it in a GraphQL client tool.
 */
export function buildMutationString<TFieldConfigKey extends string>({
  mutationName,
  fieldsConfig
}: {
  readonly mutationName: string
  readonly fieldsConfig: FieldConfigString<TFieldConfigKey>
}) {
  return `
  mutation ${mutationName}(
    ${buildArgsDefinition(fieldsConfig)}
  ) {
    ${mutationName}(
      input: {
        ${buildInputDefinition(fieldsConfig)}
      }
    ) {
      _placeholder
    }
  }
  `
}
